import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import DataTable, {
  Column,
} from "../../../components/common/data-table/DataTable";
import TitleUpdater from "../../../components/common/title/TitleUpdater";
import DeleteModal from "../../../components/common/delete-modal/DeleteModal";
import Modal from "../../../components/common/modal/Modal";
import Input from "../../../components/common/input/Input";
import Button from "../../../components/common/button/Button";
import PlusIcon from "../../../assets/images/actions/PlusIcon";
import ShowImage from "../../../components/common/show-image/ShowImage";
import { useAuth } from "../../../context/AuthContext";
import InstallmentBanksService, {
  InstallmentBank,
} from "../../../api/services/installmentBanks.service";
import { resolveMediaUrl } from "../../../utils/mediaUrl";
import styles from "./InstallmentBanks.module.scss";

/** A plan row as it is being edited: the fields are text so a half-typed
 *  number does not snap back to 0 under the cursor. */
interface TermDraft {
  months: string;
  markup_percent: string;
}

interface FormState {
  name: string;
  image: File | null;
  preview: string;
  background_color: string;
  accent_color: string;
  text_color: string;
  sort_order: string;
  is_active: boolean;
  terms: TermDraft[];
}

interface Errors {
  name?: string;
  terms?: string;
}

const EMPTY_FORM: FormState = {
  name: "",
  image: null,
  preview: "",
  background_color: "#0f172a",
  accent_color: "#e11d48",
  text_color: "#ffffff",
  sort_order: "0",
  is_active: true,
  // Most cards here advertise 3, 6 and 12 interest-free months, so a new bank
  // starts from that rather than from an empty list.
  terms: [
    { months: "3", markup_percent: "0" },
    { months: "6", markup_percent: "0" },
    { months: "12", markup_percent: "0" },
  ],
};

const InstallmentBanks = () => {
  const { t } = useTranslation("translation");
  const { setButtons, searchValue } = useAuth();

  const [banks, setBanks] = useState<InstallmentBank[]>([]);
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [isVisible, setIsVisible] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [formData, setFormData] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<Errors>({});
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");

  const getData = useCallback(async () => {
    if (!searchValue) setLoading(true);
    try {
      const res = await InstallmentBanksService.getAllBanks(
        page,
        limit,
        searchValue,
      );
      setBanks(res.data.data);
      setCount(res.data.count);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchValue]);

  useEffect(() => {
    getData();
  }, [getData]);

  const handlePageChange = (newPage: number) => {
    if (newPage < 1) return;
    if (count && newPage > Math.ceil(count / limit)) return;
    setPage(newPage);
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  };

  const closeModal = () => {
    setIsVisible(false);
    setEditId(null);
    setFormData(EMPTY_FORM);
    setErrors({});
  };

  const updateTerm = (index: number, field: keyof TermDraft, value: string) => {
    setErrors((prev) => ({ ...prev, terms: "" }));
    setFormData((prev) => ({
      ...prev,
      terms: prev.terms.map((term, i) =>
        i === index ? { ...term, [field]: value } : term,
      ),
    }));
  };

  const addTerm = () => {
    setFormData((prev) => ({
      ...prev,
      terms: [...prev.terms, { months: "", markup_percent: "0" }],
    }));
  };

  const removeTerm = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      terms: prev.terms.filter((_, i) => i !== index),
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Errors = {};

    if (!formData.name.trim()) {
      newErrors.name = t("installment_banks.errors.name_required") as string;
    }

    if (!formData.terms.length) {
      newErrors.terms = t("installment_banks.errors.terms_required") as string;
    }

    const seen = new Set<number>();
    for (const term of formData.terms) {
      const months = Number(term.months);
      const markup = Number(term.markup_percent || 0);

      if (!Number.isInteger(months) || months < 1 || months > 60) {
        newErrors.terms = t("installment_banks.errors.months_range") as string;
        break;
      }
      if (seen.has(months)) {
        newErrors.terms = t(
          "installment_banks.errors.months_duplicate",
        ) as string;
        break;
      }
      seen.add(months);

      if (Number.isNaN(markup) || markup < 0 || markup > 100) {
        newErrors.terms = t("installment_banks.errors.markup_range") as string;
        break;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      const data = new FormData();
      data.append("name", formData.name.trim());
      if (formData.image) data.append("image", formData.image);
      data.append("background_color", formData.background_color);
      data.append("accent_color", formData.accent_color);
      data.append("text_color", formData.text_color);
      data.append("sort_order", String(Number(formData.sort_order) || 0));
      data.append("is_active", String(formData.is_active));
      // The whole set travels as one JSON field: the request is multipart for
      // the card image, and repeated fields would be clumsier to read.
      data.append(
        "terms",
        JSON.stringify(
          formData.terms.map((term) => ({
            months: Number(term.months),
            markup_percent: Number(term.markup_percent || 0),
          })),
        ),
      );

      if (editId) {
        await InstallmentBanksService.updateBank(editId, data);
      } else {
        await InstallmentBanksService.createBank(data);
      }

      closeModal();
      getData();
    } catch (error: any) {
      console.error(error);
      setErrors({
        terms:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          (t("installment_banks.errors.save_failed") as string),
      });
    }
  };

  const handleEdit = async (id: number) => {
    try {
      const res = await InstallmentBanksService.getSingleBank(id);
      const bank: InstallmentBank = res.data;

      setEditId(id);
      setErrors({});
      setFormData({
        name: bank.name || "",
        image: null,
        preview: resolveMediaUrl(bank.image),
        background_color: bank.background_color || "#0f172a",
        accent_color: bank.accent_color || "#e11d48",
        text_color: bank.text_color || "#ffffff",
        sort_order: String(bank.sort_order ?? 0),
        is_active: bank.is_active,
        terms: (bank.terms || []).map((term) => ({
          months: String(term.months),
          markup_percent: String(term.markup_percent ?? 0),
        })),
      });
      setIsVisible(true);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await InstallmentBanksService.deleteBank(id);
      setDeleteModal(false);
      getData();
    } catch (error) {
      console.error(error);
    }
  };

  const columns: Column[] = [
    {
      key: "image",
      header: t("installment_banks.table.card") as string,
      render: (image, row) => {
        const src = resolveMediaUrl(image);
        return src ? (
          <div
            className={styles.card_art_wrapper}
            onClick={() => {
              setSelectedImage(src);
              setShowImageModal(true);
            }}
          >
            <img src={src} alt={row?.name} className={styles.card_art} />
          </div>
        ) : (
          <div
            className={styles.card_fallback}
            style={{
              background: row?.background_color,
              color: row?.text_color,
            }}
          >
            <span
              className={styles.card_stripe}
              style={{ background: row?.accent_color }}
            />
          </div>
        );
      },
    },
    { key: "name", header: t("installment_banks.table.name") as string },
    {
      key: "terms",
      header: t("installment_banks.table.terms") as string,
      render: (terms: InstallmentBank["terms"]) =>
        terms?.length ? (
          <div className={styles.term_chips}>
            {terms.map((term) => (
              <span key={term.months} className={styles.term_chip}>
                {t("installment_banks.months_short", { months: term.months })}
                {term.markup_percent > 0 ? ` +${term.markup_percent}%` : ""}
              </span>
            ))}
          </div>
        ) : (
          <span className={styles.no_terms}>
            {t("installment_banks.table.no_terms") as string}
          </span>
        ),
    },
    {
      key: "is_active",
      header: t("installment_banks.table.status") as string,
      render: (active: boolean) => (
        <span className={styles.status} data-active={active ? "true" : "false"}>
          {active
            ? (t("installment_banks.status.active") as string)
            : (t("installment_banks.status.hidden") as string)}
        </span>
      ),
    },
    {
      key: "sort_order",
      header: t("installment_banks.table.sort_order") as string,
    },
  ];

  useEffect(() => {
    setButtons([
      <Button
        icon={<PlusIcon />}
        key="add"
        onClick={() => {
          setFormData(EMPTY_FORM);
          setErrors({});
          setEditId(null);
          setIsVisible(true);
        }}
      >
        {t("installment_banks.add_button") as string}
      </Button>,
    ]);
    return () => setButtons([]);
  }, [setButtons, t]);

  return (
    <div>
      <TitleUpdater title={t("installment_banks.title") as string} />

      <DataTable
        table_title={t("installment_banks.title") as string}
        data={banks}
        columns={columns}
        actions
        other
        loading={loading}
        onEdit={(id) => handleEdit(id)}
        onDelete={(id) => {
          setDeleteId(id);
          setDeleteModal(true);
        }}
        paginationData={{
          total: count,
          page,
          limit,
          onPageChange: handlePageChange,
          onLimitChange: handleLimitChange,
        }}
      />

      <DeleteModal
        deleteModal={deleteModal}
        onClose={() => setDeleteModal(false)}
        onSubmit={() => handleDelete(Number(deleteId))}
        title={t("installment_banks.delete_title") as string}
      />

      <ShowImage
        imagesModal={showImageModal}
        setImagesModal={setShowImageModal}
        image={selectedImage}
      />

      <Modal
        isOpen={isVisible}
        onClose={closeModal}
        title={
          editId
            ? (t("installment_banks.edit_title") as string)
            : (t("installment_banks.add_button") as string)
        }
        onSubmit={handleSubmit}
      >
        <form className={styles.form} onSubmit={(e) => e.preventDefault()}>
          <Input
            label={t("installment_banks.form.name") as string}
            placeholder={t("installment_banks.form.name_placeholder") as string}
            value={formData.name}
            onChange={(e) => {
              setFormData({ ...formData, name: e.target.value });
              setErrors((prev) => ({ ...prev, name: "" }));
            }}
            error={errors.name}
          />

          <div className={styles.imageInputWrapper}>
            <Input
              label={t("installment_banks.form.image") as string}
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                setFormData({
                  ...formData,
                  image: file,
                  preview: file ? URL.createObjectURL(file) : formData.preview,
                });
              }}
            />
            <p className={styles.hint}>
              {t("installment_banks.form.image_hint") as string}
            </p>
            {formData.preview && (
              <img
                src={formData.preview}
                alt="preview"
                className={styles.preview}
              />
            )}
          </div>

          <div className={styles.colors}>
            <label className={styles.colorField}>
              <span>{t("installment_banks.form.background_color")}</span>
              <input
                type="color"
                value={formData.background_color}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    background_color: e.target.value,
                  })
                }
              />
            </label>
            <label className={styles.colorField}>
              <span>{t("installment_banks.form.accent_color")}</span>
              <input
                type="color"
                value={formData.accent_color}
                onChange={(e) =>
                  setFormData({ ...formData, accent_color: e.target.value })
                }
              />
            </label>
            <label className={styles.colorField}>
              <span>{t("installment_banks.form.text_color")}</span>
              <input
                type="color"
                value={formData.text_color}
                onChange={(e) =>
                  setFormData({ ...formData, text_color: e.target.value })
                }
              />
            </label>
          </div>

          <div className={styles.terms}>
            <div className={styles.termsHeader}>
              <span className={styles.termsTitle}>
                {t("installment_banks.form.terms") as string}
              </span>
              <button
                type="button"
                className={styles.addTerm}
                onClick={addTerm}
              >
                + {t("installment_banks.form.add_term") as string}
              </button>
            </div>
            <p className={styles.hint}>
              {t("installment_banks.form.terms_hint") as string}
            </p>

            {formData.terms.map((term, index) => (
              <div className={styles.termRow} key={index}>
                <label className={styles.termField}>
                  <span>{t("installment_banks.form.months")}</span>
                  <input
                    type="number"
                    min="1"
                    max="60"
                    value={term.months}
                    onChange={(e) =>
                      updateTerm(index, "months", e.target.value)
                    }
                  />
                </label>
                <label className={styles.termField}>
                  <span>{t("installment_banks.form.markup")}</span>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={term.markup_percent}
                    onChange={(e) =>
                      updateTerm(index, "markup_percent", e.target.value)
                    }
                  />
                </label>
                <button
                  type="button"
                  className={styles.removeTerm}
                  onClick={() => removeTerm(index)}
                  aria-label={t("installment_banks.form.remove_term") as string}
                >
                  ×
                </button>
              </div>
            ))}

            {errors.terms && <p className={styles.error}>{errors.terms}</p>}
          </div>

          <div className={styles.row}>
            <Input
              label={t("installment_banks.form.sort_order") as string}
              type="number"
              value={formData.sort_order}
              onChange={(e) =>
                setFormData({ ...formData, sort_order: e.target.value })
              }
            />
            <label className={styles.checkboxField}>
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) =>
                  setFormData({ ...formData, is_active: e.target.checked })
                }
              />
              <span>{t("installment_banks.form.is_active") as string}</span>
            </label>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default InstallmentBanks;
